import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Link } from 'react-router';
import { AlertTriangle, Loader2, Plus, ArrowRight } from 'lucide-react';

interface DashboardStats {
    event: {
        id: number;
        name: string;
    } | null;
    stats: {
        openDefects: number;
    };
}

export const DefectWidget = () => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3100/api';

    const { data, isLoading } = useQuery<DashboardStats>({
        queryKey: ['retreat-dashboard-stats'],
        queryFn: async () => {
            const res = await axios.get(`${apiUrl}/retreat/dashboard/stats`, { withCredentials: true });
            return res.data;
        },
    });

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center text-gray-400">
                <Loader2 className="animate-spin text-primary" />
            </div>
        );
    }

    if (!data?.event) {
        return null;
    }

    return (
        <div className="h-full flex flex-col justify-between space-y-4">
            <div className="space-y-4">
                <div className="flex items-start justify-between">
                    <div className="flex flex-col">
                        <h4 className="font-bold text-charcoal truncate max-w-[200px]" title={data.event.name}>
                            Mängel & Defekte
                        </h4>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{data.event.name}</span>
                    </div>
                    {data.stats.openDefects > 0 && (
                        <div className="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-black rounded-full uppercase border border-red-200 animate-pulse">
                            Aktiv
                        </div>
                    )}
                </div>

                <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-100 flex items-center gap-4 transition-all hover:bg-gray-50">
                    <div className="w-12 h-12 rounded-2xl bg-gold-500 text-white flex items-center justify-center shadow-lg shadow-gold-500/20">
                        <AlertTriangle size={24} />
                    </div>
                    <div className="flex-1">
                        <div className="text-2xl font-black text-charcoal">{data.stats.openDefects}</div>
                        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mt-0.5">Offene Mängel</div>
                    </div>
                </div>

                {data.stats.openDefects === 0 && (
                    <div className="text-xs text-green-600 font-bold flex items-center gap-1 bg-green-50 px-3 py-2 rounded-xl border border-green-100 italic">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        Alles in Ordnung!
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-2">
                <Link
                    to="/retreat/checkin"
                    state={{ tab: 'defects', openModal: true }}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 bg-primary text-white rounded-xl text-xs font-bold hover:bg-primary-700 transition-all border border-primary-600 shadow-md"
                >
                    <Plus size={14} />
                    Melden
                </Link>
                <Link
                    to="/retreat/checkin"
                    state={{ tab: 'defects' }}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-gray-200 text-charcoal rounded-xl text-xs font-bold hover:bg-gray-50 transition-all shadow-xs"
                >
                    Verwalten
                    <ArrowRight size={14} className="text-gray-300" />
                </Link>
            </div>
        </div>
    );
};
